import { VideoModel, VideoModelCatalog } from "../types/MeGenTypes";

/**
 * Helper ล้วน ๆ รอบ catalog โมเดล video (GET /media/video-models)
 *
 * กติกา: ไม่มีชื่อโมเดลอยู่ในโค้ดหน้าบ้าน — list/default/alias/ความสามารถ มาจาก catalog เท่านั้น
 * `catalog = null` (ยังไม่โหลด/โหลดไม่สำเร็จ) ต้องไม่ทำให้ซ่อนหรือลบข้อมูลของ user และต้องไม่บล็อกการ generate
 *
 * alias: หลังบ้าน hash **ค่าดิบ** ของ `video_model` เวลาตัดสินว่าต้อง regen วิดีโอไหม
 * ⇒ `resolveStoredVideoModel` ใช้เพื่อ "แสดงผล/ค้น catalog" เท่านั้น **ห้ามเขียนค่าที่ resolve แล้วกลับลง item**
 */

// scene ที่ helper ชุดนี้สนใจ (subset ของ ScriptWithImageItem)
export type VideoModelScene = {
  video_model?: string;
};

/** id ที่ item เก็บไว้ → id จริงของ catalog (ไม่ใช่ alias / ไม่มี catalog → คืนค่าเดิม) */
export const resolveStoredVideoModel = (
  id: string | undefined | null,
  catalog: VideoModelCatalog | null | undefined,
): string => {
  const raw = id || "";
  if (!raw) return "";
  return catalog?.aliases?.[raw] ?? raw;
};

/** ค้นทั้ง list รวม model ที่ enabled = false เพื่อ resolve label/ความสามารถ ของ id เก่าที่ item ถืออยู่ */
export const findVideoModel = (
  id: string | undefined | null,
  catalog: VideoModelCatalog | null | undefined,
): VideoModel | undefined => {
  const resolved = resolveStoredVideoModel(id, catalog);
  if (!resolved || !catalog?.models?.length) return undefined;
  return catalog.models.find((model) => model.id === resolved);
};

/** model ที่เอาไปทำ dropdown ได้ */
export const enabledVideoModels = (catalog: VideoModelCatalog | null | undefined): VideoModel[] =>
  (catalog?.models || []).filter((model) => model.enabled);

/** ป้ายของ option ว่าง — โชว์ id ของ default จาก catalog เมื่อรู้ */
export const videoDefaultLabel = (catalog: VideoModelCatalog | null | undefined): string =>
  catalog?.default ? `Use Global Default (${catalog.default})` : "Use Global Default";

/** โมเดลที่หลังบ้านจะใช้จริงกับ scene นี้ — undefined = ยังไม่รู้ (ไม่ได้เลือก + ไม่มี catalog) */
export const effectiveVideoModel = (
  item: VideoModelScene,
  catalog: VideoModelCatalog | null | undefined,
): string | undefined => resolveStoredVideoModel(item.video_model, catalog) || catalog?.default || undefined;

/**
 * โมเดลนี้ต้องมี Video Prompt ไหม
 * `null` = ตอบไม่ได้ (ไม่มี catalog / ไม่รู้จัก id / ยังไม่ได้เลือก) — ผู้เรียกต้องไม่เตือนและไม่ลบข้อมูล
 */
export const videoModelRequiresAnimPrompt = (
  id: string | undefined | null,
  catalog: VideoModelCatalog | null | undefined,
): boolean | null => {
  const model = findVideoModel(id, catalog);
  return model ? model.requires_anim_prompt : null;
};

/** โมเดลนี้เป็นแบบ lip-sync ไหม (แทนการเทียบสตริง id ตรง ๆ) — `null` = ตอบไม่ได้ */
export const isLipsyncVideoModel = (
  id: string | undefined | null,
  catalog: VideoModelCatalog | null | undefined,
): boolean | null => {
  const model = findVideoModel(id, catalog);
  return model ? model.kind === "lipsync" : null;
};

/** โมเดลนี้ใช้ character ที่เลือกไว้จริงไหม — `null` = ตอบไม่ได้ (ห้ามเตือนมั่ว) */
export const videoModelSupportsCharacter = (
  id: string | undefined | null,
  catalog: VideoModelCatalog | null | undefined,
): boolean | null => {
  const model = findVideoModel(id, catalog);
  return model ? model.supports_character : null;
};
